import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {Router} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';
// @ts-expect-error
import Bacon from 'baconjs';
import type {Location} from 'history';
import mixpanel from 'mixpanel-browser';

import {Routing} from 'nutshell-core/routing';
import type {CoreEntityType} from 'nutshell-core/types';

import {nutApolloClient} from './apollo-client';
import {
    accounts as accountsStream,
    contacts as contactsStream,
    leads as leadsStream,
} from './entities/entities';
import {EntityList} from './entity-list';
import {appHistory} from './history';
// @ts-expect-error
import {Dispatcher} from './infrastructure/dispatcher';
// @ts-expect-error
import notifications from './notifications';
import {AppRoot} from './routes/app-root';
import {store} from './store';
import {ActivityModalLegacyEventListeners} from './ui/activity-ai/activity-modal-legacy-event-listeners';
import AppEmbedded from './ui/app-embedded';
import {GetSessionUser} from './ui/core/get-session-user';
import {MinimizedReply} from './ui/email/minimized-reply';
import {EmailReplyModal} from './ui/email/sender/email-reply-modal';
import {EmailSenderRoot} from './ui/email/sender/email-sender-root';
import {TimelineEmailReplyListener} from './ui/email/sender/timeline-email-reply-listener';
import {TimelineEmailOpenListener} from './ui/email/timeline/timeline-email-open-listener';
import {VisitorsSidebarWrapper} from './ui/email-marketing/visitors/visitors-sidebar';
import {AppErrorHandler} from './ui/errors/app-error-handler';
import {GlossarySidebarWrapper} from './ui/glossary/sidebar/glossary-sidebar-wrapper';
import {FeatureLimitsModal} from './ui/limits/feature-limits-modal';
import {OpenableModalsWrapper} from './ui/openable-modals/openable-modals-wrapper';
import {PeopleIqSidebarWrapper} from './ui/people-iq';
import {ClickToCallLegacyEventListener} from './ui/phone-call/click-to-call-legacy-event-listener';
// @ts-expect-error
import user from './user';

import 'font-awesome/css/font-awesome.css';

const dispatcher = new Dispatcher();
const {stream} = dispatcher;

const defaultUrlStream = stream('url');

// Don't really have a good way of typing this.  :(
type BaconStream = any;

/**
 * @param  {string} userId - The userId of the currently logged in user
 * @param  {stream} [urlStream = defaultUrlStream] - A url stream
 * @return {Object} - A representation of application state with a start method.
 */
export default function app(userId: string, urlStream: BaconStream = defaultUrlStream) {
    // Use Mixpanel to collect heatmap data from trials. Docs below:
    // https://docs.mixpanel.com/docs/session-replay/heatmaps
    // https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web
    if (NutClientConfig.isTrial && !NutClientConfig.isDemo && !NutClientConfig.isSupportLogin) {
        mixpanel.init(NutClientConfig.mixpanelProjectToken, {
            autocapture: {
                pageview: 'full-url',
                click: true,
                input: true,
                scroll: true,
                capture_text_content: false,
            },
            persistence: 'localStorage',
            record_sessions_percent: 100, // Percentage (as an int) of sessions to record
            record_idle_timeout_ms: 900000, // end capture after 15 minutes of inactivity
            // @ts-expect-error
            record_heatmap_data: true,
        });

        const userIdNumber = userId.replace(/-users$/, '');
        const mixpanelDistinctId = `${NutshellFrontendSettings.instanceId}-${userIdNumber}`;

        mixpanel.identify(mixpanelDistinctId);
        mixpanel.people.set({
            $name: NutClientConfig.loggedInUserName,
            $email: NutClientConfig.loggedInUserEmail,
        });
    }

    // @ts-expect-error
    const accounts = accountsStream(urlStream.filter(({pathname}) => /companies$/.test(pathname)));
    // @ts-expect-error
    const contacts = contactsStream(urlStream.filter(({pathname}) => /people$/.test(pathname)));
    // @ts-expect-error
    const leads = leadsStream(urlStream.filter(({pathname}) => /leads$/.test(pathname)));

    accounts
        .changes()
        .merge(contacts.changes())
        .merge(leads.changes())
        // @ts-expect-error
        .map(({list: {query}, entityType}) => {
            const {
                queryString,
                view: {selected: selectedView},
            } = query;
            const pieces = {view: selectedView} as const;
            const combined = [queryString, Routing.param(pieces)].join('&');

            return {
                queryString: combined
                    .replace(/%5B/g, '[')
                    .replace(/%5D/g, ']')
                    .replace(/%2C/g, ','),
                entityType,
            };
        })
        .skipDuplicates(
            // @ts-expect-error
            (old, current) =>
                old.queryString === current.queryString && old.entityType === current.entityType
        )
        .skip(window.location.hash ? 1 : 0) // If there is a hash we already have our filters in the url, don't blow away the hash
        // @ts-expect-error
        .onValue(({queryString, entityType}) => {
            if (!queryString) return;

            // Check whether this is a change for the current page, because it could be coming from an old api fetch on a previous
            // page before the user client-navigated to a different list
            if (entityType === getEntityTypeFromLocation(appHistory.location)) {
                appHistory.replace(`${appHistory.location.pathname}?${queryString}`);
                // Save the current url to the server, so we can reload it when visiting
                // the root view of an entity list (clicking on "Leads" nav button for example).
                store.dispatch(EntityList.persistQueryParams(entityType, queryString));
            }
        });

    const appState = Bacon.combineTemplate({
        user: user(userId),
        notifications,

        accounts,
        contacts,
        leads,
    });

    return {
        /**
         * Bootstrapping function that attaches the UI component to the
         * DomElement provided.
         *
         * @param  {DomElement} [rootComponent] - Element to nest application UI within.
         * @return {void}
         */
        start() {
            const fullMountPoint = document.querySelector('#app-react-mount');
            if (fullMountPoint) {
                ReactDOM.render(
                    <AppErrorHandler>
                        <AppRoot appState={appState} dispatcher={dispatcher} />
                    </AppErrorHandler>,
                    fullMountPoint
                );
            }

            // Used for embedding Nutshell Marketing in MCFX
            const embeddedMountPoint = document.querySelector('#app-react-mount-embedded');
            if (embeddedMountPoint) {
                ReactDOM.render(
                    <AppErrorHandler>
                        <AppEmbedded appState={appState} dispatcher={dispatcher} />
                    </AppErrorHandler>,
                    embeddedMountPoint
                );
            }
        },
    };
}

export function getEntityTypeFromLocation(
    location: Location<unknown>
): CoreEntityType | null | undefined {
    if (location.pathname === '/leads') return 'leads';
    if (location.pathname === '/people') return 'contacts';
    if (location.pathname === '/companies') return 'accounts';
}

function attemptMounts() {
    const tooltipMount = document.createElement('div');
    if (document.body) {
        document.body.appendChild(tooltipMount);
        ReactDOM.render(
            <ReactTooltip
                delayShow={300}
                className='react-tooltip'
                effect='solid'
                multiline={true}
            />,
            tooltipMount
        );
    }

    const featureLimitsModalMount = document.createElement('div');
    featureLimitsModalMount.id = 'feature-limits-modal';
    if (document.body) {
        document.body.appendChild(featureLimitsModalMount);
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <FeatureLimitsModal />
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            featureLimitsModalMount
        );
    }

    const openableModalsMount = document.createElement('div');
    openableModalsMount.id = 'openable-modals-mount';
    if (document.body) {
        document.body.appendChild(openableModalsMount);
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <OpenableModalsWrapper />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            openableModalsMount
        );
    }

    const activityModalButtonMount = document.createElement('div');
    activityModalButtonMount.id = 'activity-modal-button-mount';
    if (document.body) {
        document.body.appendChild(activityModalButtonMount);
        ReactDOM.render(<ActivityModalLegacyEventListeners />, activityModalButtonMount);
    }

    const clickToCallPopoverMount = document.createElement('div');
    clickToCallPopoverMount.id = 'click-to-call-popover-mount';
    if (document.body) {
        document.body.appendChild(clickToCallPopoverMount);
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <ClickToCallLegacyEventListener />
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            clickToCallPopoverMount
        );
    }

    const emailSenderMount = document.querySelector('#email-sender-mount');
    if (emailSenderMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <GetSessionUser>
                            {({user: sessionUser}) => <EmailSenderRoot user={sessionUser} />}
                        </GetSessionUser>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            emailSenderMount
        );
    }

    const timelineReplyListenerMount = document.querySelector('#timeline-reply-listener-mount');
    if (timelineReplyListenerMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <TimelineEmailReplyListener />
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            timelineReplyListenerMount
        );
    }

    const emailOpenModalMount = document.querySelector('#email-open-modal-mount');
    if (emailOpenModalMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <TimelineEmailOpenListener />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            emailOpenModalMount
        );
    }

    const emailReplyModalMount = document.querySelector('#email-reply-modal-mount');
    if (emailReplyModalMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <EmailReplyModal />
                            <MinimizedReply />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            emailReplyModalMount
        );
    }

    const visitorsSidebarMount = document.querySelector('#visitors-sidebar-mount');
    if (visitorsSidebarMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <VisitorsSidebarWrapper />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            visitorsSidebarMount
        );
    }

    const glossarySidebarMount = document.querySelector('#glossary-sidebar-mount');
    if (glossarySidebarMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <GlossarySidebarWrapper />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            glossarySidebarMount
        );
    }

    const peopleIqSidebarMount = document.querySelector('#people-iq-sidebar-mount');
    if (peopleIqSidebarMount) {
        ReactDOM.render(
            <Provider store={store}>
                <ApolloProvider client={nutApolloClient}>
                    <ReactHooksApolloProvider client={nutApolloClient}>
                        <Router history={appHistory}>
                            <PeopleIqSidebarWrapper />
                        </Router>
                    </ReactHooksApolloProvider>
                </ApolloProvider>
            </Provider>,
            peopleIqSidebarMount
        );
    }
}

document.addEventListener('DOMContentLoaded', attemptMounts);
